import { useEffect, useState } from "react";
import { useStore } from "../lib/store";
import { vindOfMaakDm } from "../lib/gesprek";
import type { Conversation, PresenceKind, User } from "@swift/shared";
import {
  getHiddenConversations,
  hideConversation,
  setHiddenMarker,
  unhideConversation,
} from "../lib/settings";
import SearchBar from "./SearchBar";
import StatusBar from "./StatusBar";

type Props = {
  active: number | null;
  onSelect(id: number): void;
  onLogout(): void;
};

function presenceColor(kind: PresenceKind): string {
  switch (kind) {
    case "online":
      return "bg-brand-green";
    case "away":
      return "bg-yellow-500";
    case "busy":
      return "bg-red-500";
    default:
      return "bg-slate-500";
  }
}

export default function Sidebar({ active, onSelect, onLogout }: Props) {
  const conversations = useStore((s) => s.conversations);
  const order = useStore((s) => s.conversationOrder);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const messagesByConv = useStore((s) => s.messagesByConv);
  const [opening, setOpening] = useState<number | null>(null);
  // Map conversation_id → last_message_at op het moment van verbergen.
  const [hidden, setHidden] = useState<Map<number, string | null>>(() =>
    getHiddenConversations(),
  );

  function hide(id: number) {
    const marker = conversations[id]?.last_message_at ?? "";
    hideConversation(id, marker);
    const next = new Map(hidden);
    next.set(id, marker);
    setHidden(next);
  }
  function unhide(id: number) {
    unhideConversation(id);
    const next = new Map(hidden);
    next.delete(id);
    setHidden(next);
  }

  // Automatisch terughalen: kwam er ná het verbergen een nieuw bericht
  // binnen (last_message_at wijkt af van de marker), dan hoort het
  // gesprek weer in de lijst. Onmisbaar voor afzenders die nooit in de
  // Online-lijst staan (zoals de meldingen-bot) — die waren anders
  // permanent onbereikbaar. Werkt zowel live (socket bumpt
  // last_message_at) als bij een koude start (listConversations).
  useEffect(() => {
    let changed = false;
    const next = new Map(hidden);
    for (const [id, marker] of hidden) {
      const conv = conversations[id];
      if (!conv) continue;
      if (marker === null) {
        // Gemigreerde v1-entry: marker lazy invullen; blijft verborgen
        // tot het eerstvolgende nieuwe bericht.
        const m = conv.last_message_at ?? "";
        setHiddenMarker(id, m);
        next.set(id, m);
        changed = true;
        continue;
      }
      const current = conv.last_message_at ?? "";
      if (current !== marker) {
        unhideConversation(id);
        next.delete(id);
        changed = true;
      }
    }
    if (changed) setHidden(next);
  }, [conversations, hidden]);

  // Kanalen — uit conversations, gesorteerd op order (last_message_at desc).
  const channels: Conversation[] = order
    .map((id) => conversations[id])
    .filter((c): c is Conversation => c !== undefined && c.kind === "channel");

  // DM-conversaties — gesorteerd op recente activiteit (zelfde order-array),
  // en gefilterd op niet-verborgen. dmByOther is een lookup voor de Online-
  // lijst om de juiste conversation te koppelen aan een collega.
  const dmByOther = new Map<number, Conversation>();
  const dms: Conversation[] = [];
  for (const id of order) {
    const c = conversations[id];
    if (!c || c.kind !== "dm") continue;
    const otherId = c.member_ids.find((uid) => uid !== me?.id);
    if (otherId !== undefined) dmByOther.set(otherId, c);
    if (!hidden.has(c.id)) dms.push(c);
  }

  // Online mensen — alles wat NIET offline is, exclusief jezelf.
  const onlineUsers: User[] = Object.values(users)
    .filter((u) => u.id !== me?.id && u.status_kind !== "offline")
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  function unreadFor(c: Conversation | undefined): number {
    if (!c) return 0;
    // Server-teller heeft voorrang: die telt óók berichten die binnen-
    // kwamen terwijl de app dicht was (mobiel wordt het proces gekilled,
    // dus lokaal tellen mist alles van vóór de huidige sessie). Fallback
    // op lokaal tellen voor een server die het veld nog niet meestuurt.
    if (typeof c.unread_count === "number") return c.unread_count;
    const msgs = messagesByConv[c.id] ?? [];
    const lastSeen = c.last_read_message_id ?? 0;
    return msgs.filter((m) => m.id > lastSeen && m.sender_id !== me?.id).length;
  }

  async function openDmWith(otherId: number) {
    // Iemand opnieuw openen die je had verborgen → terughalen in de lijst. Dat
    // doet vindOfMaakDm bewust niet: die weet niets van deze lijst, en vanuit de
    // zoekbalk is openen ook geen reden om iets terug te zetten wat je weg had
    // geklikt. Hier klik je er wel degelijk op.
    const existing = dmByOther.get(otherId);
    if (existing && hidden.has(existing.id)) unhide(existing.id);

    setOpening(otherId);
    try {
      onSelect(await vindOfMaakDm(otherId));
    } finally {
      setOpening(null);
    }
  }

  return (
    <>
      <div className="border-b border-slate-700 p-2">
        <SearchBar />
      </div>

      <div className="flex-1 overflow-y-auto">
        <Header label="Kanalen" />
        {channels.length === 0 ? (
          <Empty>Geen kanalen.</Empty>
        ) : (
          <ul>
            {channels.map((c) => {
              const u = unreadFor(c);
              return (
                <li key={c.id}>
                  <button
                    className={rowClass(active === c.id)}
                    onClick={() => onSelect(c.id)}
                    title={
                      c.is_announcement
                        ? "Aankondigingskanaal — alleen admins posten"
                        : c.is_private
                        ? "Privé kanaal — alleen leden zien dit"
                        : undefined
                    }
                  >
                    <span className="text-slate-500">
                      {c.is_announcement ? "📢" : c.is_private ? "🔒" : "#"}
                    </span>
                    <span className="flex-1 truncate">{c.name ?? "kanaal"}</span>
                    {u > 0 && <Badge>{u}</Badge>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <Header label="Gesprekken" />
        {dms.length === 0 ? (
          <Empty>Nog geen gesprekken.</Empty>
        ) : (
          <ul>
            {dms.map((c) => {
              const otherId = c.member_ids.find((uid) => uid !== me?.id);
              const other = otherId !== undefined ? users[otherId] : undefined;
              const unread = unreadFor(c);
              return (
                <li key={c.id} className="group relative">
                  <button
                    className={rowClass(active === c.id)}
                    onClick={() => onSelect(c.id)}
                    title={other?.status_text ?? undefined}
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${presenceColor(
                        other?.status_kind ?? "offline",
                      )}`}
                    />
                    <span className="flex-1 truncate">
                      {other?.display_name ?? "(verwijderde gebruiker)"}
                      {other && (
                        <span className="ml-1 text-slate-500">@{other.username}</span>
                      )}
                    </span>
                    {unread > 0 && <Badge>{unread}</Badge>}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      hide(c.id);
                    }}
                    title="Verberg uit lijst (geschiedenis blijft bewaard)"
                    aria-label="Verberg gesprek"
                    className="absolute right-1 top-1/2 hidden h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-slate-500 hover:bg-red-900/40 hover:text-red-400 group-hover:inline-flex"
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <Header label="Online" />
        {onlineUsers.length === 0 ? (
          <Empty>Niemand online.</Empty>
        ) : (
          <ul>
            {onlineUsers.map((u) => {
              const dm = dmByOther.get(u.id);
              const unread = unreadFor(dm);
              const isOpening = opening === u.id;
              return (
                <li key={u.id}>
                  <button
                    className={rowClass(dm ? active === dm.id : false)}
                    onClick={() => void openDmWith(u.id)}
                    disabled={isOpening}
                    title={u.status_text ?? undefined}
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${presenceColor(
                        u.status_kind,
                      )}`}
                    />
                    <span className="flex-1 truncate">
                      {u.display_name}
                      <span className="ml-1 text-slate-500">@{u.username}</span>
                    </span>
                    {unread > 0 && <Badge>{unread}</Badge>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <StatusBar onLogout={onLogout} />
    </>
  );
}

function rowClass(activeRow: boolean): string {
  return `flex w-full items-center gap-2 px-3 py-0.5 text-left text-sm leading-tight hover:bg-slate-700 disabled:opacity-50 ${
    activeRow ? "bg-slate-700 text-slate-100" : "text-slate-300"
  }`;
}

function Header({ label }: { label: string }) {
  return (
    <div className="mt-1 px-3 pb-0 pt-1 text-[10px] font-semibold uppercase tracking-wider leading-tight text-slate-500">
      {label}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-3 py-0.5 text-xs italic leading-tight text-slate-500">{children}</div>;
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-white">
      {children}
    </span>
  );
}
